import React from 'react';
import { Container, Card, Button, Title, ScoreGrid, ScoreCard } from '../../styles/GlobalStyles';

const GameHistory = ({ gameHistory, onBack, onDeleteGame, translations, language }) => {
  const t = translations[language];

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getWinner = (players) => {
    if (!players || players.length === 0) return null;
    return players.reduce((winner, player) =>
      player.score > winner.score ? player : winner
    );
  };

  const handleDeleteGame = (gameId) => {
    if (window.confirm(language === 'nl' ? 'Weet je zeker dat je dit spel wilt verwijderen?' : 'Are you sure you want to delete this game?')) {
      onDeleteGame(gameId);
    }
  };

  if (gameHistory.length === 0) {
    return (
      <Container>
        <Title>{t.history}</Title>
        <Card>
          <div style={{
            textAlign: 'center',
            padding: '20px 10px',
            color: '#666',
            background: '#f8f9fa',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {t.noGamesYet}
            </div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              Start je eerste spel om de geschiedenis te zien!
            </div>
          </div>
          <Button variant="secondary" onClick={onBack} style={{ marginTop: '8px' }}>
            {t.back}
          </Button>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Title>{t.history}</Title>

      {gameHistory.map(game => {
        const winner = getWinner(game.players);
        return (
          <Card key={game.id} style={{ padding: '12px', marginBottom: '8px' }}>
            {/* Game Header - Compact */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
              paddingBottom: '8px',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <div>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#333',
                  fontFamily: 'monospace'
                }}>
                  {formatDate(game.dateStarted)}
                </div>
                <div style={{
                  fontSize: '10px',
                  color: '#666',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginTop: '2px'
                }}>
                  {game.rounds.length} {t.rounds} • {game.isCompleted ? t.completed : t.inProgress}
                </div>
              </div>
              <Button
                variant="danger"
                onClick={() => handleDeleteGame(game.id)}
                style={{
                  width: '32px',
                  height: '32px',
                  margin: 0,
                  padding: 0,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </Button>
            </div>

            {/* Scores Grid - Numbers First */}
            <ScoreGrid style={{ gap: '4px', marginBottom: '12px' }}>
              {game.players.map(player => (
                <div
                  key={player.id}
                  style={{
                    background: winner?.id === player.id ? '#f0f0f0' : '#fff',
                    border: winner?.id === player.id ? '2px solid #000' : '1px solid #ccc',
                    padding: '8px 4px',
                    textAlign: 'center'
                  }}
                >
                  <div style={{
                    fontSize: '28px',
                    fontWeight: '900',
                    color: '#000',
                    lineHeight: '1',
                    fontFamily: 'monospace',
                    marginBottom: '4px'
                  }}>
                    {Math.round(player.score)}
                  </div>
                  <div style={{
                    fontSize: '9px',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {player.name}
                  </div>
                  {winner?.id === player.id && (
                    <div style={{
                      fontSize: '8px',
                      color: '#000',
                      marginTop: '2px',
                      fontWeight: '600'
                    }}>
                      WIN
                    </div>
                  )}
                </div>
              ))}
            </ScoreGrid>

            {/* Round Details - Compact Accordion */}
            {game.rounds.length > 0 && (
              <details style={{ marginTop: '8px' }}>
                <summary style={{
                  cursor: 'pointer',
                  fontSize: '10px',
                  color: '#666',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  padding: '4px 0',
                  borderTop: '1px solid #e0e0e0',
                  paddingTop: '8px'
                }}>
                  {language === 'nl' ? 'BEKIJK RONDES' : 'VIEW ROUNDS'} ({game.rounds.length})
                </summary>
                <div style={{
                  marginTop: '8px',
                  maxHeight: '150px',
                  overflowY: 'auto',
                  border: '1px solid #e0e0e0',
                  background: '#f8f9fa'
                }}>
                  {game.rounds.map((round, index) => (
                    <div key={round.id} style={{
                      background: '#fff',
                      borderBottom: '1px solid #e0e0e0',
                      padding: '6px 8px',
                      borderLeft: `3px solid ${round.won ? '#000' : '#666'}`
                    }}>
                      <div style={{
                        fontSize: '10px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{ fontWeight: '600' }}>
                          {index + 1}. {round.gameType.name}
                        </span>
                        <span style={{
                          color: round.won ? '#000' : '#666',
                          fontSize: '9px',
                          textTransform: 'uppercase',
                          fontWeight: '600'
                        }}>
                          {round.won ? (language === 'nl' ? 'WIN' : 'WIN') : (language === 'nl' ? 'VERLIES' : 'LOSS')}
                        </span>
                      </div>
                      <div style={{
                        fontSize: '9px',
                        color: '#666',
                        marginTop: '2px'
                      }}>
                        {language === 'nl' ? 'Initiatiefnemer(s)' : 'Initiator(s)'}: {round.initiators.map(id =>
                          game.players.find(p => p.id === id)?.name
                        ).join(', ')}
                        {round.tricksWon !== undefined && ` • ${language === 'nl' ? 'Slagen' : 'Tricks'}: ${round.tricksWon}`}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </Card>
        );
      })}

      <Card style={{ padding: '8px' }}>
        <Button variant="secondary" onClick={onBack}>
          {t.back}
        </Button>
      </Card>
    </Container>
  );
};

export default GameHistory;
